import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { NotificationProvider } from "@/context/NotificationContext";
import { UIProvider } from "@/context/UIContext";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import POAccept from "@/pages/POAccept";
import DrawingShare from "@/pages/DrawingShare";
import Dashboard from "@/pages/Dashboard";
import TasksList from "@/pages/TasksList";
import TaskCreate from "@/pages/TaskCreate";
import TaskDetail from "@/pages/TaskDetail";
import TeamMembers from "@/pages/TeamMembers";
import Masters from "@/pages/Masters";
import Extensions from "@/pages/Extensions";
import Reports from "@/pages/Reports";
import AuditLog from "@/pages/AuditLog";
import NotificationsPage from "@/pages/NotificationsPage";
import Orders from "@/pages/Orders";
import OrderDetail from "@/pages/OrderDetail";
import OrderCosting from "@/pages/OrderCosting";
import WorkflowTemplates from "@/pages/WorkflowTemplates";
import Vendors from "@/pages/Vendors";
import Customers from "@/pages/Customers";
import AccessRequests from "@/pages/AccessRequests";
import PurchaseOrders from "@/pages/PurchaseOrders";
import StoreReceipts from "@/pages/StoreReceipts";
import CompanySettings from "@/pages/CompanySettings";
import Permissions from "@/pages/Permissions";
import { hasModule } from "@/lib/permissions";

function Protected({ children, module, adminOnly }) {
  const { user } = useAuth();
  if (user === null)
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-950 text-emerald-600 font-mono text-sm">
        Initialising portal…
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/" replace />;
  if (module && !hasModule(user, module)) return <Navigate to="/" replace />;
  return <Layout>{children}</Layout>;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
      <NotificationProvider>
      <UIProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/po-accept/:token" element={<POAccept />} />
            <Route path="/d/:token" element={<DrawingShare />} />
            <Route path="/" element={<Protected><Dashboard /></Protected>} />
            <Route path="/tasks" element={<Protected module="tasks"><TasksList /></Protected>} />
            <Route path="/tasks/new" element={<Protected module="tasks"><TaskCreate /></Protected>} />
            <Route path="/tasks/:id" element={<Protected module="tasks"><TaskDetail /></Protected>} />
            <Route path="/orders" element={<Protected module="orders"><Orders /></Protected>} />
            <Route path="/orders/:id" element={<Protected module="orders"><OrderDetail /></Protected>} />
            <Route path="/orders/:id/costing" element={<Protected module="orders"><OrderCosting /></Protected>} />
            <Route path="/workflows" element={<Protected module="workflows"><WorkflowTemplates /></Protected>} />
            <Route path="/team" element={<Protected module="team"><TeamMembers /></Protected>} />
            <Route path="/vendors" element={<Protected module="vendors"><Vendors /></Protected>} />
            <Route path="/purchase-orders" element={<Protected module="vendors"><PurchaseOrders /></Protected>} />
            <Route path="/material-receipts" element={<Protected><StoreReceipts /></Protected>} />
            <Route path="/settings" element={<Protected adminOnly><CompanySettings /></Protected>} />
            <Route path="/masters" element={<Protected module="masters"><Masters /></Protected>} />
            <Route path="/customers" element={<Protected module="customers"><Customers /></Protected>} />
            <Route path="/extensions" element={<Protected module="extensions"><Extensions /></Protected>} />
            <Route path="/reports" element={<Protected module="reports"><Reports /></Protected>} />
            <Route path="/audit" element={<Protected module="audit"><AuditLog /></Protected>} />
            <Route path="/permissions" element={<Protected adminOnly><Permissions /></Protected>} />
            <Route path="/access-requests" element={<Protected><AccessRequests /></Protected>} />
            <Route path="/notifications" element={<Protected><NotificationsPage /></Protected>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" theme="light" richColors />
      </UIProvider>
      </NotificationProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
